/* eslint-disable @typescript-eslint/naming-convention */
import { ID } from '@ynomia/core/dist/blueprint';

export enum SYSTEM_TYPE {
  CREATE_ASSET_OBSERVATION = 'ynomia::system::create_asset_observation',
  STATUS_UPDATE_OBSERVATION = 'ynomia::system::status_update_observation',
  AUTOMATIC_STATUS_UPDATE_OBSERVATION = 'ynomia::system::automatic_status_update_observation',
  AUTOFILL_STATUS_UPDATE_OBSERVATION = 'ynomia::system::autofill_status_update_observation',
  DEVICE_ASSIGNED_OBSERVATION = 'ynomia::system::device_assigned_observation',
  DEVICE_UNASSIGNED_OBSERVATION = 'ynomia::system::device_unassigned_observation',
  ELEMENT_SWAPPED_OBSERVATION = 'ynomia::system::element_swapped_observation',
  DESTINATION_UPDATE = 'ynomia::system::destination_update_observation',
  ADD_TO_PARENT_OBSERVATION = 'ynomia::system::add_to_parent_observation',
  REMOVE_FROM_PARENT_OBSERVATION = 'ynomia::system::remove_from_parent_observation',
  UPDATE_CHILDREN_OBSERVATION = 'ynomia::system::update_children_observation',
}

export type RawObservationRecord = {
  _id: string;
  created: string;
  date?: string;
  isBackdated?: boolean;
  batchID?: string;
  from?: string;
  monitorableID: string;
  deviceID?: string;
  deviceUDID?: string;
  source: string;
  order: number;
  to?: string;
  type: SYSTEM_TYPE | string;
  userID?: string;
  isOutOfSequence?: boolean;
  isCurrentStatus?: boolean;
  fields?: { [key: string]: any };
};

export type Asset = ID & {
  ready: boolean; // Custom mobile property: has the asset reached the backend?
  isFetching?: boolean; // Custom mobile property: is the asset data currently being refreshed?
  trackable?: {
    // No idea why this is an array. But it is.
    tracking: Array<{
      alt: number;
      lat: string; // Why are these not numbers
      lon: string; // Why is this not `lng`.
      layers: Array<string>;
      rtLayers?: Array<string>;
      updatedAt: Date;
    }>;
  };
  observations?: Array<RawObservationRecord>;
  isArchived?: boolean;
};

export type TwinData = {
  lastFetched: Date | null
  hexColorsKeyedByTwinId: { [twinID: string]: string }
};

export type AssetTableColumn = {
  key: string
  title: string
  filter_enabled: boolean
  sort_enabled: boolean
  custom?: string
  layerType?: string
  parentSlot?: {
    label: string
    type: string
  }
  properties?: {
    numberFormat?: string
  }
  fixed?: string,
  observationType?: string,
  aggregateMetric?: string,
  default_sort?: 'asc' | 'desc',
};

export type ObservationAction = 'NONE' | 'OPEN_ASSET_TYPE_OBSERVATION' | 'VIEW_OBSERVATION' |
'VIEW_BOOKING' | 'VIEW_SWAP' | 'VIEW_DESTINATION_UPDATE' | 'VIEW_CHILDREN_SLOT_OBSERVATION';

// Common fields for all summary configurations
type SummaryConfigBase = {
  type: string,
  id: string,
  title: string,
};

export type PlanSummaryConfig = SummaryConfigBase & {
  type: 'plan',
  planId: string,
};

export type StatusSummaryConfig = SummaryConfigBase & {
  type: 'status_summary',
  mode: 'current' | 'cumulative' | 'both',
  hide_current: boolean
  status_sections: {
    title: string,
    index: number
  }[],
  force_show_all: boolean
};

export type LocationSummaryConfig = SummaryConfigBase & {
  type: 'location_summary',
  /** Single slot to locate assets by. Kept for existing configs. */
  locationSlotId?: string,
  /**
   * Several slots to locate assets by. An asset's location is the union of
   * the layers reached through every listed slot, so an asset counts toward
   * a layer if any of its slots reaches it. Takes precedence over
   * locationSlotId when both are set.
   */
  locationSlotIds?: Array<string>,
  fieldFilter?: { [key: string]: Array<string> },
};

export type LocationSlot = {
  id: string,
  direction: 'children' | 'parents',
};

export type FieldSummaryConfig = SummaryConfigBase & {
  type: 'field_summary',
  generateOptionsFromAssetList?: boolean,
  customIcon?: string,
  field: string,
};

export type ObservationSummaryConfig = SummaryConfigBase & {
  type: 'observation_summary',
  observationTypes: Array<string>
};

export type SummaryConfig = PlanSummaryConfig
| StatusSummaryConfig
| FieldSummaryConfig
| LocationSummaryConfig
| ObservationSummaryConfig;

export type ObservationListItem = {
  observation_id?: string;
  observation_raw?: RawObservationRecord;
  timestamp: string;
  server_timestamp?: Date | string | number;
  timestamp_human_readable: string;
  title: string;
  description?: string;
  statusColor: string;
  icon: {
    name: string;
    type: string;
  };
  action: string,
  canDelete: boolean;
  fontColor?: string | null;
  tooltip?: string;
  isAutoFill?: boolean;
  canEditDate?: boolean;
  excludeTime?: boolean;
};

export type AssetUpdate = {
  label: string,
  layerID: string,
  twinID: string,
  type: string,
  fields: Record<string, string>
};
